/** Knowledge graph console (M28, GR-G3-2): summary, items, one item's drawer, merge and rebuild. */
export const knowledgeGraph = {
  title: "Knowledge graph",
  subtitle:
    "The offices, programs, fees and other things your sources mention, and how they connect. Correct a name, merge duplicates or hide what your assistant should not use.",
  stage: "Experiment",
  loading: "Loading the knowledge graph",
  error: {
    title: "We couldn't load the knowledge graph",
    body: "Your sources and your assistant's answers are not affected. Try again.",
    retry: "Try again",
  },
  access: {
    viewOnly:
      "You can look at the graph but not change it. Ask a workspace owner for permission to edit knowledge if you need to rename, hide, merge or add names.",
    rebuildOnly:
      "You can edit items but not rebuild the graph. Ask a workspace owner for permission to re-index knowledge.",
    support: "This is a read-only support session, so nothing here can be changed.",
    supportReason: "Read-only during a support session",
    needEdit: "You need permission to edit knowledge",
    needRebuild: "You need permission to re-index knowledge",
  },
  off: {
    on: "On",
    off: "Off",
    layersLabel: "The layers that decide whether the graph runs",
    layers: {
      platform_flag: "Platform rollout",
      plan: "Plan",
      tenant_override: "Workspace switch",
      agent: "Assistant setting",
    },
    knowledgeCta: "Open the Knowledge Base",
    billingCta: "See plans",
    agentCta: "Open assistant settings",
    blockedBy: "Switched off at: {{layer}}",
    regionLabel: "The knowledge graph is off",
    platform_flag: {
      title: "The knowledge graph isn't switched on yet",
      body: "It is an experiment, rolled out workspace by workspace. Nothing is missing on your side and your answers work as before. Ask the Sagot team if you would like early access.",
    },
    plan: {
      title: "The knowledge graph is part of the Pro plan",
      body: "Your current plan doesn't include it. Upgrade to let your assistant follow the connections between offices, programs and fees across your sources. Your sources and answers keep working as they do now.",
    },
    tenant_override: {
      title: "The knowledge graph is switched off for this workspace",
      body: "A Sagot administrator switched it off for this workspace, so nothing is read into the graph. Your sources and answers work as before. Ask the Sagot team to switch it back on.",
    },
    agent: {
      title: "No assistant uses the knowledge graph yet",
      body: "The graph is allowed for this workspace, but every assistant has it set to Off. In an assistant's Retrieval settings, set the knowledge graph to Assist; your sources are then read into the graph.",
    },
  },
  noAssistant: {
    body: "No assistant is set to use the graph, so new and re-read sources are not added to it. What is here stays for you to review.",
    cta: "Open assistant settings",
  },
  empty: {
    regionLabel: "No graph yet",
    title: "No graph yet",
    body: "The graph fills in as your sources are read. Add a source to your Knowledge Base, or read the ones you have again.",
    bodyNoAssistant:
      "Nothing is read into the graph until an assistant is set to use it. Switch it on in an assistant's Retrieval settings, then read your sources.",
    filteredTitle: "No items match",
    filteredBody: "Try another word, or clear the filters to see every item.",
    nothingVisibleTitle: "No visible items",
    nothingVisibleBody: "Every item is hidden or merged. Show all items to review them.",
  },
  summary: {
    label: "Knowledge graph totals",
    items: "Items",
    itemsHint: "your assistant can use",
    relations: "Connections",
    relationsHint: "between items",
    hidden: "Hidden",
    hiddenHint: "kept out of answers",
    merged: "Merged",
    mergedHint: "folded into another item",
    lastRun: "Last reading",
    never: "Nothing has been read into the graph yet.",
    run: {
      queued: "Waiting to start",
      running: "Reading your sources",
      succeeded: "Finished",
      failed: "Did not finish",
      deferred: "Paused: budget used",
      deferredSize: "Paused: workspace too large",
    },
    runValue: "{{chunks}} chunks read · {{items}} new items · {{relations}} new connections",
    runFailed: "The graph from the reading before it is kept. Reason: {{reason}}",
    runSizeLimit: "This workspace has more active chunks than the graph can serve well, so it is not built or rebuilt. Remove sources to continue; the graph you already have keeps working.",
    cooldown: "You can rebuild again in {{minutes}} min.",
    budget: "Reading budget",
    budgetLabel: "Chunks read in {{month}}",
    budgetValue: "{{used}} of {{limit}}",
    budgetHint: "Every chunk read into the graph counts. The budget starts again each month (UTC).",
    budgetUsedUp: "This month's budget is used up. Sources wait until next month.",
    budgetNone: "This plan has no monthly reading budget.",
  },
  filters: {
    search: "Search items and other names",
    searchLabel: "Search items",
    type: "Filter by type",
    allTypes: "All types",
    typeOption: "{{type}} ({{count}})",
    sort: "Sort items",
    sortOptions: { mentions: "Most mentioned", name: "Name, A to Z", recent: "Recently changed" },
    status: "Show items",
    statusOptions: { active: "Visible", hidden: "Hidden", merged: "Merged", all: "All" },
    clear: "Clear filters",
    showAll: "Show all items",
  },
  list: {
    label: "Items in the knowledge graph",
    errorTitle: "We couldn't load the items",
    hidden: "Hidden",
    merged: "Merged",
    decided: "Your decision",
    decidedHint: "Kept when the graph is rebuilt",
    open: "Open {{name}}",
    names_one: "{{count}} other name",
    names_other: "{{count}} other names",
    item: "Item",
    type: "Type",
    mentions: "Mentions",
    relations: "Connections",
    changed: "Changed",
    mentionsCount_one: "{{count}} mention",
    mentionsCount_other: "{{count}} mentions",
    relationsCount_one: "{{count}} connection",
    relationsCount_other: "{{count}} connections",
    showing: "Showing {{from}} to {{to}} of {{total}}",
    pages: "Item pages",
    previous: "Previous page",
    next: "Next page",
    page: "Page {{n}}",
    pageOf: "Page {{page}} of {{count}}",
  },
  drawer: {
    loading: "Loading the item",
    description: "{{type}} in your knowledge graph",
    notFoundTitle: "This item isn't in the graph",
    notFoundBody: "It may have been cleared by a rebuild, or the link is old.",
    backToList: "Back to the list",
    errorTitle: "We couldn't load this item",
    mentions: "Mentions",
    relations: "Connections",
    names: "Other names",
    firstSeen: "First seen",
    changed: "Last change",
    visible: "Visible",
    hiddenNote:
      "Hidden: your assistant does not use this item and the graph will not create it again. Show it again to rename or merge it.",
    decidedNote: "You decided on this item. A rebuild keeps your decision.",
    mergedInto: "Merged into “{{name}}”. It stays here as a pointer.",
    mergedNote: "This item was merged into another one.",
    openSurvivor: "Open “{{name}}”",
  },
  actions: {
    rename: "Rename",
    hide: "Hide",
    show: "Show again",
    merge: "Merge into…",
    hiddenDone: "“{{name}}” is hidden.",
    shown: "“{{name}}” is visible again.",
  },
  aliases: {
    title: "Other names",
    hint: "Other spellings people use for this item (up to {{max}}). A question that uses one still finds it.",
    none: "No other names yet.",
    addLabel: "Add another name",
    placeholder: "For example, a short form",
    language: "Language",
    languages: { any: "Any language", en: "English", fil: "Filipino", taglish: "Taglish" },
    add: "Add name",
    added: "Added “{{alias}}”.",
    remove: "Remove {{alias}}",
    removed: "Removed “{{alias}}”. The graph won't add it back.",
  },
  relations: {
    title: "Connections",
    hint: "How this item connects to others, strongest first, with the chunks that say so.",
    none: "No connections yet.",
    outgoing: "This item connects to:",
    incoming: "Connects to this item from:",
    evidence_one: "{{count}} chunk",
    evidence_other: "{{count}} chunks",
    capped: "Showing the first {{count}} connections.",
  },
  evidence: {
    title: "Where it is stated",
    hint: "Chunks of your sources that name this item or state one of its connections. Only references are shown, not the text.",
    none: "No chunks point to this item yet.",
    mention: "Names it",
    relation: "States: {{relation}}",
    position: "Chunk {{ordinal}}",
    page: "Page {{page}}",
    excluded: "Left out of answers",
    capped: "Showing the first {{count}} chunks.",
  },
  names: {
    empty: "Enter a name with at least one letter or number.",
    long: "Use {{max}} characters or fewer.",
    chars: "A name can't contain < > { } [ ] | \\ ` or double quotes.",
    same: "That is already this item's name.",
  },
  rename: {
    title: "Rename “{{name}}”",
    body: "The old name stays as another name, so a question that uses it still finds this item.",
    label: "New name",
    hint: "Up to {{max}} characters. Use roles, places and things, not the names of people.",
    save: "Save name",
    saved: "Renamed to “{{name}}”.",
    openTaken: "Open the item with that name",
  },
  merge: {
    title: "Merge “{{name}}” into another item",
    step: { target: "Choose the item to keep", review: "Check what moves", done: "Merged" },
    sameType: "Only visible items of the same type ({{type}}) can be merged.",
    search: "Type a name to narrow the list",
    searchLabel: "Search items to keep",
    resultsLabel: "Items you can keep",
    none: "No other visible item of this type matches.",
    errorTitle: "We couldn't load the items",
    review: "Review",
    back: "Back",
    apply: "Merge items",
    openTarget: "Open “{{name}}”",
    reviewIntro: "“{{source}}” will be folded into “{{target}}”. This moves:",
    movesLabel: "What moves",
    moveMentions_one: "{{count}} chunk that names “{{source}}” will name “{{target}}” instead.",
    moveMentions_other: "{{count}} chunks that name “{{source}}” will name “{{target}}” instead.",
    moveNames_one: "{{count}} other name, and “{{source}}” itself, become other names of “{{target}}” (up to {{max}}).",
    moveNames_other: "{{count}} other names, and “{{source}}” itself, become other names of “{{target}}” (up to {{max}}).",
    moveRelations_one: "{{count}} connection moves to “{{target}}”. One it already has is joined; one between the two is removed.",
    moveRelations_other: "{{count}} connections move to “{{target}}”. Ones it already has are joined; ones between the two are removed.",
    after:
      "“{{source}}” stays in the graph as a pointer to “{{target}}” (see the Merged filter), so nothing breaks. A merge can't be undone from here, and your decision is kept when the graph is rebuilt.",
    doneTitle: "Merged",
    doneBody: "“{{source}}” is now part of “{{target}}”.",
    doneMentions_one: "{{count}} mention moved.",
    doneMentions_other: "{{count}} mentions moved.",
    doneNames_one: "{{count}} other name moved.",
    doneNames_other: "{{count}} other names moved.",
    doneRelations: "Connections: {{moved}} moved, {{joined}} joined with one that already existed, {{removed}} removed.",
    doneDropped_one: "{{count}} other name was left behind because the item was full.",
    doneDropped_other: "{{count}} other names were left behind because the item was full.",
  },
  rebuild: {
    action: "Rebuild graph",
    title: "Rebuild the knowledge graph?",
    body: "Every source is read again and the items it finds replace the ones from the last reading. It takes a few minutes and you can keep working.",
    keeps: "Your own decisions stay: renames, hidden items, merges and other names.",
    cost: "It uses your monthly reading budget.",
    costWithBudget: "It uses your monthly reading budget ({{used}} of {{limit}} chunks used so far).",
    cooldown: "One rebuild every {{minutes}} minutes.",
    confirm: "Rebuild",
    queued: "Rebuild started. The numbers update as it runs.",
    waiting: "You can rebuild again in {{minutes}} min",
    noAssistant: "Set an assistant to use the graph first",
  },
  errors: {
    aliasExists: "This item already has that other name.",
    nameTaken: "Another item of this type already has that name.",
    aliasLimit: "This item already has {{max}} other names. Remove one first.",
    entityState: "This item is hidden or merged, so it can't be changed this way. Show it again first.",
    notFound: "This item is no longer in the graph. The list was refreshed.",
    notReady: "The graph reader isn't available on this deployment yet. Try again later.",
    disabled: {
      platform_flag: "The knowledge graph was switched off while you were here. Reload the page to see why.",
      plan: "The knowledge graph is not part of your plan. Reload the page to see why.",
      tenant_override: "The knowledge graph was switched off for this workspace. Reload the page to see why.",
      agent: "Set an assistant to use the knowledge graph first, then try again.",
    },
    merge: {
      type_mismatch: "Only items of the same type can be merged.",
      not_active: "Only visible items can be merged. Show both items again first.",
      self: "An item can't be merged into itself.",
    },
    cooldown: "A rebuild was requested a moment ago. Try again in {{minutes}} min.",
    name: "Enter a name of up to {{max}} characters.",
  },
} as const;
